import {HTMLAttributes, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useState} from "react";
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    CircularProgress,
    ListItem,
    ListItemText,
    TextField
} from "@mui/material";
import {FieldProps} from "formik";
import {DataRequestParams, Filter} from "./types";
import {useQuery} from "react-query";
import {useDebounce} from "use-debounce";

type LoadResult<T> = {
    items: T[];
    page: number;
    limit: number;
    totalPages: number;
    totalElements: number;
};

interface GenericSelectProps<T, TFilter extends Filter = any> extends FieldProps {
    label: string;
    required?: boolean;
    disabled?: boolean;
    setSelected: (value: string) => void;
    options?: T[];
    loadOptions?: (params: DataRequestParams<TFilter>, filter: TFilter) => Promise<LoadResult<any>>;
    getOptionLabel: (option: T) => string;
    getSecondaryLabel?: (option: T) => string;
    renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T, children: ReactNode) => ReactNode;
    setOnValueChange?: (valueChange: boolean) => void;
    initialSelected?: T;
    sort?: string;
    filterOptions?: (options: T[], state: { inputValue: string }) => T[];
}

function GenericSelect<T, TFilter extends Filter = any>({
                                                            label,
                                                            required,
                                                            disabled,
                                                            field,
                                                            form,
                                                            options = [],
                                                            loadOptions,
                                                            getOptionLabel,
                                                            getSecondaryLabel,
                                                            setSelected,
                                                            renderOption,
                                                            setOnValueChange,
                                                            initialSelected,
                                                            sort,
                                                            filterOptions,
                                                        }: GenericSelectProps<T, TFilter>) {
    const [inputValue, setInputValue] = useState<string>("");
    const [hasChanged, setHasChanged] = useState<boolean>(false);
    const [oldField, setOldField] = useState<string>(field.value || "");
    const [asyncOptions, setAsyncOptions] = useState<T[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [initialized, setInitialized] = useState<boolean>(false);

    useEffect(() => {
        if (initialized || !initialSelected) return;

        const id = (initialSelected as any).id;
        const labelText = getOptionLabel(initialSelected);

        setInputValue(labelText);

        if (!field.value || field.value !== id) {
            setSelected(id);
            form.setFieldValue(field.name, id);
        }
        setAsyncOptions(prev => {
            const exists = prev.find(opt => (opt as any).id === id);
            return exists ? prev : [initialSelected, ...prev];
        });

        setInitialized(true);
    }, [
        initialized,
        initialSelected,
        field.value,
        field.name,
        form,
        setSelected,
        getOptionLabel
    ]);

    const debouncedInputValue = useDebounce(inputValue, 300)[0];

    const { isLoading, data, refetch } = useQuery(
        ['options', debouncedInputValue, sort],
        async () => {
            if (!loadOptions) return {
                items: [],
                page: 0,
                limit: 20,
                totalPages: 0,
                totalElements: 0
            } as LoadResult<T>;

            const filter = {search: debouncedInputValue} as unknown as TFilter;
            const response = await loadOptions(
                {
                    ...filter,
                    page: 0,
                    limit: 20,
                    sort: sort,
                } as DataRequestParams<TFilter>,
                filter
            );
            return response as LoadResult<T>;
        },
        {
            enabled: !!loadOptions && debouncedInputValue !== undefined,
            staleTime: 0,
            cacheTime: 300000,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    );

    useEffect(() => {
        if (data?.items) {
            setAsyncOptions(data.items);
        }
        setLoading(isLoading);
    }, [data, isLoading]);

    useEffect((): void => {
        if (!hasChanged || !setOnValueChange) return;

        setOnValueChange(oldField !== field.value);
    }, [field.value, oldField, setOnValueChange, hasChanged]);

    const optionList = useMemo(() =>
            loadOptions ? asyncOptions : options
        , [loadOptions, asyncOptions, options]);

    const selectedOption = useMemo(() => {
        return optionList.find(opt => {
            const optId = (opt as any).id;
            const optLabel = getOptionLabel(opt);
            return optId === field.value && optLabel === inputValue;
        }) || null;
    }, [optionList, field.value, inputValue, getOptionLabel]);

    const handleOnInputChange = useCallback((_event: SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
        refetch().then(() => {});

        if (newInputValue === "") {
            setSelected("");
            form.setFieldValue(field.name, "");
        }
    }, [refetch, setSelected, form, field.name]);

    const handleOnChange = useCallback((_event: SyntheticEvent, value: T | null) => {
        setHasChanged(true);
        setOldField(field.value);

        if (value) {
            const labelText = getOptionLabel(value);
            const id = (value as any).id;
            setInputValue(labelText);
            setSelected(id);
            form.setFieldValue(field.name, id);
        } else {

            setInputValue("");
            setSelected("");
            form.setFieldValue(field.name, "");
        }
    }, [field.value, getOptionLabel, setSelected, form, field.name]);

    return (
        <Autocomplete
            size="medium"
            disabled={disabled}
            options={optionList}
            getOptionLabel={getOptionLabel}
            filterOptions={filterOptions ?? ((x) => x)}
            loading={loading}
            inputValue={inputValue}
            onInputChange={handleOnInputChange}
            value={selectedOption}
            onChange={handleOnChange}
            renderOption={(props, option) =>
                renderOption ? renderOption(props, option, (
                    <ListItemText primary={getOptionLabel(option)} secondary={getSecondaryLabel?.(option)}/>
                )) : (
                    <ListItem {...props} key={(option as any).id}>
                        <ListItemText primary={getOptionLabel(option)} secondary={getSecondaryLabel?.(option)}/>
                    </ListItem>
                )
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                    {...params}
                    label={label + (required ? " *" : "")}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={20}/> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    );
}

export default GenericSelect;

